import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import CommonSelect, { Option } from '../../common/commonSelect/CommonSelect';
import { useDivisionOptions, useTeamOptions } from '../../../state/master';
import { apiClient } from '../../../lib/apiClient';
import ConfirmDialog from '../../common/ConfirmDialog';

type Mode = 'default' | 'copy' | '';

export type ColumnInitializeResult = {
  mode: 'default' | 'copy';
  copyDivisionId: number;
  copyTeamId: number;
  copySameEnlightConnection: boolean;
  reset: boolean;
};

type Props = {
  open: boolean;
  division: number;
  team: number;
  onClose: () => void;
  onSubmit: (result: ColumnInitializeResult) => Promise<void> | void;
  canEdit?: boolean;
};

const normalizeEnlightTeam = (v: any): string => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const lc = s.toLowerCase();
  if (lc === 'none' || lc === 'null') return '';
  return s;
};

const extractEnlightTeamFromDocset = (data: any): string => {
  const arr = Array.isArray(data)
    ? data
    : Array.isArray((data as any)?.list)
      ? (data as any).list
      : Array.isArray((data as any)?.data)
        ? (data as any).data
        : data
          ? [data]
          : [];
  if (!Array.isArray(arr) || arr.length === 0) {
    return normalizeEnlightTeam((data as any)?.enlight_team ?? (data as any)?.enlightTeam);
  }
  const hit = arr.find((x: any) => normalizeEnlightTeam((x as any)?.enlight_team ?? (x as any)?.enlightTeam));
  if (!hit) return '';
  return normalizeEnlightTeam((hit as any)?.enlight_team ?? (hit as any)?.enlightTeam);
};

const ColumnInitializeDialog: React.FC<Props> = ({ open, division, team, onClose, onSubmit, canEdit = true }) => {
  const { divisionOptions } = useDivisionOptions();
  const { teamOptions } = useTeamOptions();

  const [mode, setMode] = React.useState<Mode>('');
  const [copyDivisionId, setCopyDivisionId] = React.useState<number>(0);
  const [copyTeamId, setCopyTeamId] = React.useState<number>(0);
  const [copySameEnlightConnection, setCopySameEnlightConnection] = React.useState<boolean>(false);
  const [linkedEnlightTeamName, setLinkedEnlightTeamName] = React.useState<string>('');
  const [loadingLinkedEnlight, setLoadingLinkedEnlight] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [keepCurrent, setKeepCurrent] = React.useState<boolean>(false);
  const [resetConfirmOpen, setResetConfirmOpen] = React.useState<boolean>(false);

  const prevOpenRef = React.useRef<boolean>(false);
  React.useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;
    setMode('');
    setCopyDivisionId(Number(division ?? 0));
    setCopyTeamId(0);
    setCopySameEnlightConnection(false);
    setLinkedEnlightTeamName('');
    setSubmitting(false);
    setKeepCurrent(false);
    setResetConfirmOpen(false);
  }, [open, division]);

  const reset = !keepCurrent;

  const isCopyToSelf = Number(copyDivisionId) === Number(division) && Number(copyTeamId) === Number(team);

  React.useEffect(() => {
    if (!open || mode !== 'copy' || !copyDivisionId || !copyTeamId) {
      setLinkedEnlightTeamName('');
      setLoadingLinkedEnlight(false);
      setCopySameEnlightConnection(false);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoadingLinkedEnlight(true);
      try {
        const res = await apiClient.get('/get_team_docset_list', {
          params: { department_id: Number(copyDivisionId) || 0, team_id: Number(copyTeamId) || 0 },
        });
        if (cancelled) return;
        const name = extractEnlightTeamFromDocset(res?.data);
        setLinkedEnlightTeamName(name);
        setCopySameEnlightConnection(Boolean(name));
      } catch (e) {
        if (cancelled) return;
        console.warn('get_team_docset_list failed in ColumnInitializeDialog', e);
        setLinkedEnlightTeamName('');
        setCopySameEnlightConnection(false);
      } finally {
        if (!cancelled) setLoadingLinkedEnlight(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, mode, copyDivisionId, copyTeamId]);

  const divisionSelectOptions: Option[] = React.useMemo(() => {
    return (divisionOptions ?? []).map((row: any) => ({
      value: String(row?.department_id ?? row?.value ?? ''),
      label: String(row?.label ?? ''),
    }));
  }, [divisionOptions]);

  const filteredTeamOptions = React.useMemo(() => {
    return (teamOptions ?? []).filter((row: any) => {
      if (Number(row?.department_id) !== Number(copyDivisionId)) return false;
      // 参照元として自分自身のチームは選択できないため、選択肢から除外する
      if (Number(copyDivisionId) === Number(division) && Number(row?.team_id) === Number(team)) return false;
      return true;
    });
  }, [teamOptions, copyDivisionId, division, team]);

  const teamSelectOptions: Option[] = React.useMemo(() => {
    return (filteredTeamOptions ?? []).map((row: any) => ({
      value: String(row?.team_id ?? row?.value ?? ''),
      label: String(row?.label ?? ''),
    }));
  }, [filteredTeamOptions]);

  const selectedDivision: Option | null =
    copyDivisionId ? (divisionSelectOptions.find((o) => Number(o.value) === Number(copyDivisionId)) ?? null) : null;
  const selectedTeam: Option | null =
    copyTeamId ? (teamSelectOptions.find((o) => Number(o.value) === Number(copyTeamId)) ?? null) : null;

  const handleDivisionChange = (opt: Option | null) => {
    const nextDivision = Number(opt?.value ?? 0);
    setCopyDivisionId(nextDivision);
    const nextTeams = (teamOptions ?? []).filter((row: any) => {
      if (Number(row?.department_id) !== Number(nextDivision)) return false;
      if (Number(nextDivision) === Number(division) && Number(row?.team_id) === Number(team)) return false;
      return true;
    });
    setCopyTeamId(nextTeams.length > 0 ? Number((nextTeams[0] as any)?.team_id ?? 0) : 0);
  };

  const handleTeamChange = (opt: Option | null) => {
    setCopyTeamId(Number(opt?.value ?? 0));
  };

  const okDisabled =
    submitting ||
    mode === '' ||
    (mode === 'copy' && (!copyDivisionId || !copyTeamId || !selectedDivision || !selectedTeam || isCopyToSelf));

  const performSubmit = async () => {
    if (mode === '') return;
    setSubmitting(true);
    try {
      await onSubmit({
        mode,
        copyDivisionId: mode === 'copy' ? copyDivisionId : 0,
        copyTeamId: mode === 'copy' ? copyTeamId : 0,
        copySameEnlightConnection: mode === 'copy' ? Boolean(copySameEnlightConnection && linkedEnlightTeamName) : false,
        reset,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOk = () => {
    if (mode === '') return;
    if (reset) {
      setResetConfirmOpen(true);
      return;
    }
    void performSubmit();
  };

  const handleResetConfirm = () => {
    setResetConfirmOpen(false);
    void performSubmit();
  };

  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>全項目の再設定</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">
            このチームの列設定を再登録します。対象範囲はモードによって異なります。
          </Typography>

          <FormControl>
            <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              <FormControlLabel
                value="default"
                control={<Radio />}
                label="デフォルト値に戻す"
                disabled={!canEdit}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1, display: 'block' }}>
                対象は共通項目（週報分類・Project・ロット・仕向け・パワートレイン・自由入力欄など）のみです。不足している共通項目だけを追加し、既存の設定・フリー項目には影響しません。
              </Typography>
              <FormControlLabel
                value="copy"
                control={<Radio />}
                label="他のチームから設定をコピーする"
                disabled={!canEdit}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                共通項目とフリー項目の両方を、参照元の課・チームからまとめてコピーします。
              </Typography>
            </RadioGroup>
          </FormControl>

          <Divider sx={{ mt: 1 }} />

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              追加オプション（どちらの選択でも指定できます）
            </Typography>
            <Box sx={{ backgroundColor: '#fffbeb', borderRadius: 1, p: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={keepCurrent}
                    onChange={(e) => setKeepCurrent(Boolean(e.target.checked))}
                    disabled={!canEdit}
                    color="warning"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                    現在の項目も保持
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{ display: 'block', ml: 4, color: 'rgba(0, 0, 0, 0.6)' }}>
                現設定内容を保持したまま、項目を追加します。
                <br />
                ※ 過去に週報で実際に使用された項目は、チェックの有無にかかわらずデータ保護のため更新されず、現状のまま残ります。
              </Typography>
            </Box>
          </Box>

          {mode === 'copy' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                （※参照元の課・チームの共通項目・フリー項目をまとめてコピーします。同名の既存項目・選択肢は上書きされます。参照元のマスタ設定が後で更新されても、本チームの内容には反映されません）
              </Typography>

              <Grid container spacing={2}>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 0.5, fontSize: '0.75rem' }}>参照する課</FormLabel>
                    <CommonSelect
                      id="column-init-division"
                      options={divisionSelectOptions}
                      value={selectedDivision}
                      onChange={handleDivisionChange}
                      placeholder="課を入力して絞り込み..."
                      isClearable={false}
                      isSearchable={true}
                      allowCreate={false}
                      isDisabled={!canEdit}
                      menuPlacement="top"
                    />
                  </FormControl>
                </Grid>
                <Grid size={6}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 0.5, fontSize: '0.75rem' }}>チーム</FormLabel>
                    <CommonSelect
                      id="column-init-team"
                      options={teamSelectOptions}
                      value={selectedTeam}
                      onChange={handleTeamChange}
                      placeholder="チームを入力して絞り込み..."
                      isClearable={false}
                      isSearchable={true}
                      allowCreate={false}
                      isDisabled={!canEdit}
                      menuPlacement="top"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {isCopyToSelf && (
                <Typography variant="caption" color="error">
                  現在のチーム自身は参照元に選択できません。
                </Typography>
              )}

              {!isCopyToSelf && copyDivisionId > 0 && copyTeamId > 0 && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid size={3}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>EnlightID連携</Typography>
                    </Grid>
                    <Grid size={5}>
                      {loadingLinkedEnlight ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">読込中...</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color={linkedEnlightTeamName ? 'text.primary' : 'text.secondary'}>
                          {linkedEnlightTeamName || '未設定'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid size={4}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={copySameEnlightConnection}
                            onChange={(e) => setCopySameEnlightConnection(Boolean(e.target.checked))}
                            disabled={!canEdit || loadingLinkedEnlight || !linkedEnlightTeamName}
                          />
                        }
                        label={<Typography variant="caption">同じEnlightID連携を設定する</Typography>}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        <Button variant="contained" disabled={!canEdit || okDisabled} onClick={handleOk}>
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'OK'}
        </Button>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting}>
          キャンセル
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={resetConfirmOpen}
        title="現在の登録内容を削除しますか？"
        description={
          `現在登録されている${mode === 'default' ? '共通項目' : '共通項目・フリー項目'}の内容を削除してから、` +
          `${mode === 'default' ? 'デフォルト値で' : '選択した課・チームの内容で'}登録し直します。この操作は元に戻せません。` +
          '（過去に週報で使用された項目は削除されず残ります）本当に実行しますか？'
        }
        confirmText="削除して実行"
        confirmColor="error"
        onConfirm={handleResetConfirm}
        onClose={() => setResetConfirmOpen(false)}
        processing={submitting}
      />
    </Dialog>
  );
};

export default ColumnInitializeDialog;
